import type {
  HrAnnouncementItem,
  HrAnnouncementReach,
  HrAnnouncementsList,
  HrAnnouncementsQuery,
  HrBatchDocumentsBody,
  HrCreateAnnouncementBody,
  HrCreateDocumentBody,
  HrDecisionResult,
  HrDistributeDocumentBody,
  HrDocumentAccesses,
  HrDocumentItem,
  HrDocumentsList,
  HrDocumentsQuery,
  HrMetrics,
  HrRequestDetail,
  HrRequestListItem,
  HrRequestsList,
  HrRequestsQuery,
  HrRespondBody,
  HrVacationItem,
  HrVacationsList,
  HrVacationsQuery,
} from "@evolua/api-client";

/**
 * Fila RH — fixtures demo (FASE-06-T49).
 * Estado mutável em memória para atribuir/responder no modo demo.
 */

const ANA = "11111111-1111-4111-8111-111111111111";

let queue: HrRequestListItem[] = [
  {
    id: "aaaaaaaa-1111-4111-8111-aaaaaaaaaaaa",
    type: "CORRECTION_REQUEST",
    label: "Correção: endereco — Ana Silva",
    requesterName: "Ana Silva",
    employeeId: ANA,
    status: "IN_PROGRESS",
    stepKey: "hr-approval",
    stepStatus: "ACTIVE",
    assignedSubjects: ["demo-hr"],
    assigneeSubject: null,
    dueAt: "2026-08-14T18:00:00.000Z",
    createdAt: "2026-08-12T10:00:00.000Z",
    waitingHours: 4,
    overdue: false,
    href: "/solicitacoes",
  },
  {
    id: "bbbbbbbb-2222-4222-8222-bbbbbbbbbbbb",
    type: "VACATION",
    label: "Férias 2026-11-01–2026-11-25 (25 dias)",
    requesterName: "Ana Silva",
    employeeId: ANA,
    status: "IN_PROGRESS",
    stepKey: "hr-review",
    stepStatus: "ACTIVE",
    assignedSubjects: ["demo-hr"],
    assigneeSubject: "demo-hr",
    dueAt: "2026-08-20T18:00:00.000Z",
    createdAt: "2026-08-11T10:00:00.000Z",
    waitingHours: 28,
    overdue: false,
    href: "/solicitacoes",
  },
];

const historyById = new Map<string, HrRequestDetail["history"]>();
const attachmentsById = new Map<string, HrRequestDetail["attachments"]>();

function filterQueue(query: HrRequestsQuery): HrRequestListItem[] {
  let items = queue.map((i) => ({ ...i }));
  if (query.type) items = items.filter((i) => i.type === query.type);
  if (query.status) items = items.filter((i) => i.status === query.status);
  else items = items.filter((i) => i.status === "IN_PROGRESS");
  if (query.assignee === "me") {
    items = items.filter(
      (i) => i.assigneeSubject === "demo-hr" || i.assignedSubjects.includes("demo-hr"),
    );
  } else if (query.assignee === "unassigned") {
    items = items.filter((i) => i.assignedSubjects.length > 1 || !i.assigneeSubject);
  } else if (query.assignee) {
    items = items.filter(
      (i) =>
        i.assigneeSubject === query.assignee ||
        i.assignedSubjects.includes(query.assignee!),
    );
  }
  if (query.overdue === true) items = items.filter((i) => i.overdue);
  if (query.q?.trim()) {
    const q = query.q.trim().toLowerCase();
    items = items.filter(
      (i) =>
        i.requesterName.toLowerCase().includes(q) ||
        i.label.toLowerCase().includes(q) ||
        i.type.toLowerCase().includes(q),
    );
  }
  return items;
}

export function listDemoHrRequests(query: HrRequestsQuery = {}): HrRequestsList {
  const data = filterQueue(query);
  return { data, total: data.length };
}

export function getDemoHrRequest(id: string): HrRequestDetail {
  const item = queue.find((i) => i.id === id);
  if (!item) throw new Error("Solicitação não encontrada");
  return {
    ...item,
    payload: { seed: true },
    history: historyById.get(id) ?? [
      {
        id: `hist-${id}`,
        at: item.createdAt,
        actorSubject: "demo-employee",
        action: "STARTED",
        metadata: {},
      },
    ],
    attachments: attachmentsById.get(id) ?? [],
    metrics: {
      firstResponseHours: item.assigneeSubject ? 1.5 : null,
      resolutionHours: item.status === "IN_PROGRESS" ? null : 6,
      slaHours: 48,
      breached: item.overdue,
    },
  };
}

export function getDemoHrMetrics(): HrMetrics {
  const open = queue.filter((i) => i.status === "IN_PROGRESS");
  return {
    openCount: open.length,
    overdueCount: open.filter((i) => i.overdue).length,
    closedCount: queue.filter((i) => i.status !== "IN_PROGRESS").length,
    avgFirstResponseHours: 2.4,
    avgResolutionHours: null,
  };
}

function pushHistory(
  id: string,
  action: string,
  metadata: Record<string, unknown> = {},
): void {
  const prev = historyById.get(id) ?? [];
  historyById.set(id, [
    ...prev,
    {
      id: `hist-${id}-${prev.length}`,
      at: new Date().toISOString(),
      actorSubject: "demo-hr",
      action,
      metadata,
    },
  ]);
}

export function assignDemoHrRequest(
  id: string,
  body: { assigneeSubject?: string; comment?: string } = {},
): HrDecisionResult {
  const item = queue.find((i) => i.id === id);
  if (!item) throw new Error("Solicitação não encontrada");
  const assignee = body.assigneeSubject?.trim() || "demo-hr";
  item.assigneeSubject = assignee;
  item.assignedSubjects = [assignee];
  pushHistory(id, "REASSIGNED", { to: [assignee], reason: body.comment ?? "claim" });
  return { id, status: item.status, notificationId: `notif-assign-${id}` };
}

export function reassignDemoHrRequest(
  id: string,
  body: { assigneeSubject: string; reason: string },
): HrDecisionResult {
  const item = queue.find((i) => i.id === id);
  if (!item) throw new Error("Solicitação não encontrada");
  item.assigneeSubject = body.assigneeSubject;
  item.assignedSubjects = [body.assigneeSubject];
  pushHistory(id, "REASSIGNED", {
    to: [body.assigneeSubject],
    reason: body.reason,
  });
  return { id, status: item.status, notificationId: `notif-reassign-${id}` };
}

export function respondDemoHrRequest(id: string, body: HrRespondBody): HrDecisionResult {
  const item = queue.find((i) => i.id === id);
  if (!item) throw new Error("Solicitação não encontrada");
  if (body.attachmentObjectIds?.length) {
    attachmentsById.set(
      id,
      body.attachmentObjectIds.map((objectId) => ({ objectId })),
    );
  }
  if (body.decision === "REJECT") {
    if (!body.comment.trim()) throw new Error("Justificativa obrigatória");
    item.status = "REJECTED";
    pushHistory(id, "REJECTED", { comment: body.comment });
  } else {
    item.status = "APPROVED";
    pushHistory(id, "APPROVED", { comment: body.comment });
  }
  queue = queue.filter((i) => i.id !== id || i.status === "IN_PROGRESS");
  // keep closed items out of default queue but retain for detail if needed
  if (item.status !== "IN_PROGRESS") {
    queue = queue.filter((i) => i.id !== id);
  }
  return {
    id,
    status: item.status,
    notificationId: `notif-respond-${id}`,
  };
}

export function approveDemoHrRequest(
  id: string,
  body: { comment?: string } = {},
): HrDecisionResult {
  return respondDemoHrRequest(id, {
    comment: body.comment?.trim() || "Resolvido pelo RH",
    decision: "APPROVE",
  });
}

export function rejectDemoHrRequest(
  id: string,
  body: { comment: string },
): HrDecisionResult {
  return respondDemoHrRequest(id, { comment: body.comment, decision: "REJECT" });
}

let vacationQueue: HrVacationItem[] = [
  {
    id: "cccccccc-3333-4333-8333-cccccccccccc",
    requesterName: "Ana Silva",
    employeeId: ANA,
    companyId: "aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaaa",
    departmentId: "bbbbbbbb-bbbb-4bbb-8bbb-bbbbbbbbbbbb",
    departmentName: "RH",
    status: "IN_PROGRESS",
    stepKey: "hr-review",
    assignedSubjects: ["demo-hr"],
    startDate: "2026-11-01",
    endDate: "2026-11-25",
    days: 25,
    waitingOnManager: false,
    hasConflict: true,
    conflicts: [
      {
        otherId: "dddddddd-4444-4444-8444-dddddddddddd",
        otherRequesterName: "Ana Silva",
        overlapStart: "2026-11-10",
        overlapEnd: "2026-11-14",
      },
    ],
    createdAt: "2026-08-11T10:00:00.000Z",
    href: "/ferias-rh",
  },
  {
    id: "dddddddd-4444-4444-8444-dddddddddddd",
    requesterName: "Ana Silva",
    employeeId: ANA,
    companyId: "aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaaa",
    departmentId: "bbbbbbbb-bbbb-4bbb-8bbb-bbbbbbbbbbbb",
    departmentName: "RH",
    status: "IN_PROGRESS",
    stepKey: "manager-approval",
    assignedSubjects: ["demo-manager"],
    startDate: "2026-11-10",
    endDate: "2026-11-14",
    days: 5,
    waitingOnManager: true,
    hasConflict: true,
    conflicts: [
      {
        otherId: "cccccccc-3333-4333-8333-cccccccccccc",
        otherRequesterName: "Ana Silva",
        overlapStart: "2026-11-10",
        overlapEnd: "2026-11-14",
      },
    ],
    createdAt: "2026-08-12T09:00:00.000Z",
    href: "/ferias-rh",
  },
];

export function listDemoHrVacations(query: HrVacationsQuery = {}): HrVacationsList {
  let data = vacationQueue.map((i) => ({ ...i }));
  if (query.status) data = data.filter((i) => i.status === query.status);
  if (query.departmentId) {
    data = data.filter((i) => i.departmentId === query.departmentId);
  }
  if (query.q?.trim()) {
    const q = query.q.trim().toLowerCase();
    data = data.filter(
      (i) =>
        i.requesterName.toLowerCase().includes(q) ||
        i.departmentName.toLowerCase().includes(q),
    );
  }
  return {
    data,
    total: data.length,
    conflictCount: data.filter((i) => i.hasConflict).length,
  };
}

export function approveDemoHrVacationOnBehalf(
  id: string,
  body: { reason: string; comment?: string },
): HrDecisionResult {
  if (!body.reason.trim()) throw new Error("Motivo obrigatório");
  const item = vacationQueue.find((i) => i.id === id);
  if (!item) throw new Error("Solicitação não encontrada");
  item.status = "APPROVED";
  item.waitingOnManager = false;
  vacationQueue = vacationQueue.filter((i) => i.id !== id || i.status === "IN_PROGRESS");
  vacationQueue = vacationQueue.filter((i) => i.id !== id);
  return { id, status: "APPROVED", notificationId: `notif-onbehalf-${id}` };
}

const RH_DEPARTMENT_ID = "bbbbbbbb-bbbb-4bbb-8bbb-bbbbbbbbbbbb";
const ANA_ID = "11111111-1111-4111-8111-111111111111";

let hrDocs: HrDocumentItem[] = [
  {
    id: "aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaa01",
    name: "Código de conduta 2026",
    category: "POLICY",
    objectKey: "mock/documents/codigo-conduta.txt",
    filename: "codigo-conduta-2026.txt",
    issuedAt: "2026-01-01",
    expiresAt: null,
    expired: false,
    employeeIds: [],
    departmentId: null,
    accessCount: 0,
    createdAt: new Date().toISOString(),
    createdBy: "demo-hr",
  },
  {
    id: "bbbbbbbb-bbbb-4bbb-8bbb-bbbbbbbbbb02",
    name: "Certificado NR-05 vencido",
    category: "HEALTH",
    objectKey: "mock/documents/nr05.txt",
    filename: "nr05-vencido.txt",
    issuedAt: "2024-01-01",
    expiresAt: "2025-12-31",
    expired: true,
    employeeIds: [ANA_ID],
    departmentId: RH_DEPARTMENT_ID,
    accessCount: 0,
    createdAt: new Date().toISOString(),
    createdBy: "demo-hr",
  },
];

const hrDocAccesses: HrDocumentAccesses["data"] = [];

export function listDemoHrDocuments(query: HrDocumentsQuery = {}): HrDocumentsList {
  let data = hrDocs.map((d) => ({ ...d }));
  if (query.category) data = data.filter((d) => d.category === query.category);
  if (query.departmentId) {
    data = data.filter((d) => d.departmentId === query.departmentId);
  }
  if (query.employeeId) {
    data = data.filter((d) => d.employeeIds.includes(query.employeeId!));
  }
  if (query.expired === true) data = data.filter((d) => d.expired);
  if (query.expired === false) data = data.filter((d) => !d.expired);
  if (query.q?.trim()) {
    const q = query.q.trim().toLowerCase();
    data = data.filter(
      (d) => d.name.toLowerCase().includes(q) || d.category.toLowerCase().includes(q),
    );
  }
  return {
    data,
    total: data.length,
    expiredCount: data.filter((d) => d.expired).length,
  };
}

export function createDemoHrDocument(body: HrCreateDocumentBody): HrDocumentItem {
  const id = crypto.randomUUID();
  const item: HrDocumentItem = {
    id,
    name: body.name,
    category: body.category,
    objectKey: body.objectKey ?? `mock/documents/${id}.txt`,
    filename: body.filename ?? `${body.name}.txt`,
    issuedAt: body.issuedAt ?? new Date().toISOString().slice(0, 10),
    expiresAt: body.expiresAt ?? null,
    expired: Boolean(
      body.expiresAt && body.expiresAt < new Date().toISOString().slice(0, 10),
    ),
    employeeIds: [...(body.employeeIds ?? [])],
    departmentId: null,
    accessCount: 0,
    createdAt: new Date().toISOString(),
    createdBy: "demo-hr",
  };
  hrDocs = [...hrDocs, item];
  return { ...item };
}

export function batchDemoHrDocuments(body: HrBatchDocumentsBody): HrDocumentsList {
  const created = body.items.map((item) => createDemoHrDocument(item));
  if (body.distributeDepartmentId) {
    for (const doc of created) {
      distributeDemoHrDocument(doc.id, { departmentId: body.distributeDepartmentId });
    }
  }
  const data = created.map((c) => hrDocs.find((d) => d.id === c.id)!);
  return {
    data,
    total: data.length,
    expiredCount: data.filter((d) => d.expired).length,
  };
}

export function distributeDemoHrDocument(
  id: string,
  body: HrDistributeDocumentBody,
): HrDocumentItem {
  const doc = hrDocs.find((d) => d.id === id);
  if (!doc) throw new Error("Documento não encontrado");
  if (body.departmentId) {
    doc.departmentId = body.departmentId;
    if (body.departmentId === RH_DEPARTMENT_ID && !doc.employeeIds.includes(ANA_ID)) {
      doc.employeeIds = [...doc.employeeIds, ANA_ID];
    }
  }
  for (const eid of body.employeeIds ?? []) {
    if (!doc.employeeIds.includes(eid)) doc.employeeIds = [...doc.employeeIds, eid];
  }
  return { ...doc };
}

export function listDemoHrDocumentAccesses(id: string): HrDocumentAccesses {
  const data = hrDocAccesses.filter((a) => a.documentId === id);
  return { data, total: data.length };
}

const RH_COMPANY_ID = "aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaaa";

const hrAnnouncements: HrAnnouncementItem[] = [
  {
    id: "cccccccc-cccc-4ccc-8ccc-cccccccccc01",
    title: "Campanha de vacinação 2026",
    body: "A vacinação contra a gripe ocorre na semana de 18/08 no escritório. Confirme ciência.",
    status: "PUBLISHED",
    segment: {
      companyId: RH_COMPANY_ID,
      departmentId: RH_DEPARTMENT_ID,
      positionId: null,
    },
    scheduledAt: null,
    publishedAt: "2026-08-10T12:00:00.000Z",
    audienceCount: 1,
    readCount: 0,
    ackCount: 0,
    createdAt: "2026-08-10T11:00:00.000Z",
    createdBy: "demo-hr",
  },
  {
    id: "cccccccc-cccc-4ccc-8ccc-cccccccccc02",
    title: "Comunicado agendado — política de home office",
    body: "Nova política entra em vigor na próxima segunda. (seed agendamento T52)",
    status: "SCHEDULED",
    segment: { companyId: null, departmentId: null, positionId: null },
    scheduledAt: "2099-01-01T12:00:00.000Z",
    publishedAt: null,
    audienceCount: 0,
    readCount: 0,
    ackCount: 0,
    createdAt: "2026-08-11T09:00:00.000Z",
    createdBy: "demo-hr",
  },
];

const hrReachById = new Map<string, HrAnnouncementReach>();

function ensureDemoReach(id: string): HrAnnouncementReach {
  let reach = hrReachById.get(id);
  if (!reach) {
    reach = {
      announcementId: id,
      targeted: 1,
      read: 0,
      acked: 0,
      items: [{ employeeId: ANA_ID, readAt: null, ackedAt: null }],
    };
    hrReachById.set(id, reach);
  }
  return reach;
}

export function listDemoHrAnnouncements(
  query: HrAnnouncementsQuery = {},
): HrAnnouncementsList {
  let data = hrAnnouncements.map((a) => ({ ...a, segment: { ...a.segment } }));
  if (query.status) data = data.filter((a) => a.status === query.status);
  if (query.q?.trim()) {
    const q = query.q.trim().toLowerCase();
    data = data.filter(
      (a) => a.title.toLowerCase().includes(q) || a.body.toLowerCase().includes(q),
    );
  }
  return { data, total: data.length };
}

export function createDemoHrAnnouncement(
  body: HrCreateAnnouncementBody,
): HrAnnouncementItem {
  const id = crypto.randomUUID();
  const scheduledAt = body.scheduledAt ?? null;
  const scheduleInFuture = scheduledAt !== null && Date.parse(scheduledAt) > Date.now();
  const item: HrAnnouncementItem = {
    id,
    title: body.title,
    body: body.body,
    status: body.publishNow ? "PUBLISHED" : scheduleInFuture ? "SCHEDULED" : "DRAFT",
    segment: {
      companyId: body.companyId ?? null,
      departmentId: body.departmentId ?? null,
      positionId: body.positionId ?? null,
    },
    scheduledAt,
    publishedAt: body.publishNow ? new Date().toISOString() : null,
    audienceCount: body.publishNow ? 1 : 0,
    readCount: 0,
    ackCount: 0,
    createdAt: new Date().toISOString(),
    createdBy: "demo-hr",
  };
  hrAnnouncements.unshift(item);
  if (body.publishNow) {
    ensureDemoReach(id);
  }
  return { ...item, segment: { ...item.segment } };
}

export function getDemoHrAnnouncement(id: string): HrAnnouncementItem {
  const item = hrAnnouncements.find((a) => a.id === id);
  if (!item) throw new Error("Comunicado não encontrado");
  return { ...item, segment: { ...item.segment } };
}

export function publishDemoHrAnnouncement(id: string): HrAnnouncementItem {
  const item = hrAnnouncements.find((a) => a.id === id);
  if (!item) throw new Error("Comunicado não encontrado");
  item.status = "PUBLISHED";
  item.publishedAt = item.publishedAt ?? new Date().toISOString();
  item.audienceCount = Math.max(item.audienceCount, 1);
  ensureDemoReach(id);
  return { ...item, segment: { ...item.segment } };
}

export function getDemoHrAnnouncementReach(id: string): HrAnnouncementReach {
  if (!hrAnnouncements.some((a) => a.id === id)) {
    throw new Error("Comunicado não encontrado");
  }
  return { ...ensureDemoReach(id), items: [...ensureDemoReach(id).items] };
}
